import { Image } from 'image-js'

/**
 * This file was mostly written by Claude, proceed with caution.
 * Claude was not familiar with the 1.0.0 release of image-js which came out 2 weeks ago as of this writing, so it uses the 0.37.0 version.
 */

interface BackgroundRemovalOptions {
  /** Tolerance for color matching (0-255) */
  tolerance?: number
  /** Whether to use the corner pixels to determine background color */
  useCornerDetection?: boolean
  /** Specific background color to remove (RGB array) */
  backgroundColor?: [number, number, number]
  /** Whether to apply edge smoothing */
  smoothEdges?: boolean
}

/**
 * Removes background color from an image and returns a PNG with transparency
 * @param imageUrl - URL of the input image
 * @param options - Configuration options for background removal
 * @returns Promise<Blob> - PNG blob with transparent background
 */
export async function removeBackgroundColor(
  imageUrl: string,
  options: BackgroundRemovalOptions = {}
): Promise<Blob> {
  const { tolerance = 30, useCornerDetection = true, backgroundColor, smoothEdges = true } = options

  try {
    // Load the image
    const image = await Image.load(imageUrl)

    // Ensure we're working with RGBA format
    const rgbaImage = image.alpha ? image : image.rgba8()

    // Determine background color
    const bgColor =
      backgroundColor || (useCornerDetection ? detectBackgroundColor(rgbaImage) : [255, 255, 255]) // default to white

    // Remove background
    const resultImage = removeBackground(rgbaImage, bgColor, tolerance, smoothEdges)

    // Convert to PNG blob
    const pngBuffer = await resultImage.toBlob('image/png')

    return pngBuffer
  } catch (error) {
    throw new Error(`Failed to process image: ${error}`)
  }
}

/**
 * Detects the most common background color from corner pixels
 */
function detectBackgroundColor(image: Image): [number, number, number] {
  const width = image.width
  const height = image.height
  const cornerPixels: Array<[number, number, number]> = []

  // Sample corner pixels (each corner gets a 3x3 sample)
  const corners = [
    { x: 0, y: 0 }, // top-left
    { x: width - 3, y: 0 }, // top-right
    { x: 0, y: height - 3 }, // bottom-left
    { x: width - 3, y: height - 3 }, // bottom-right
  ]

  corners.forEach((corner) => {
    for (let dx = 0; dx < 3; dx++) {
      for (let dy = 0; dy < 3; dy++) {
        const x = Math.max(0, Math.min(width - 1, corner.x + dx))
        const y = Math.max(0, Math.min(height - 1, corner.y + dy))
        const pixel = image.getPixelXY(x, y)
        cornerPixels.push([pixel[0] ?? 0, pixel[1] ?? 0, pixel[2] ?? 0])
      }
    }
  })

  // Find most common color
  return findMostCommonColor(cornerPixels)
}

/**
 * Finds the most common color from an array of RGB values
 */
function findMostCommonColor(pixels: Array<[number, number, number]>): [number, number, number] {
  const colorCounts = new Map<string, { count: number; color: [number, number, number] }>()

  pixels.forEach(([r, g, b]) => {
    // Group similar colors together (reduce precision for better matching)
    const key = `${Math.floor(r / 5) * 5},${Math.floor(g / 5) * 5},${Math.floor(b / 5) * 5}`

    if (colorCounts.has(key)) {
      colorCounts.get(key)!.count++
    } else {
      colorCounts.set(key, { count: 1, color: [r, g, b] })
    }
  })

  // Return the most common color
  let maxCount = 0
  let mostCommonColor: [number, number, number] = [255, 255, 255]

  for (const { count, color } of colorCounts.values()) {
    if (count > maxCount) {
      maxCount = count
      mostCommonColor = color
    }
  }

  return mostCommonColor
}

/**
 * Removes background color from image
 */
function removeBackground(
  image: Image,
  backgroundColor: [number, number, number],
  tolerance: number,
  smoothEdges: boolean
): Image {
  const width = image.width
  const height = image.height
  const [bgR, bgG, bgB] = backgroundColor

  // Create new image data
  const newData = new Uint8Array(width * height * 4)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const pixel = image.getPixelXY(x, y)
      const [r, g, b, a] = pixel
      const index = (y * width + x) * 4

      // Calculate color distance
      const distance = Math.sqrt(
        Math.pow((r ?? 0) - bgR, 2) + Math.pow((g ?? 0) - bgG, 2) + Math.pow((b ?? 0) - bgB, 2)
      )

      if (distance <= tolerance) {
        // Make transparent
        newData[index] = r ?? 0 // R
        newData[index + 1] = g ?? 0 // G
        newData[index + 2] = b ?? 0 // B
        newData[index + 3] = 0 // A (transparent)
      } else {
        // Keep original pixel
        newData[index] = r ?? 0 // R
        newData[index + 1] = g ?? 0 // G
        newData[index + 2] = b ?? 0 // B
        newData[index + 3] = smoothEdges
          ? calculateSmoothAlpha(distance, tolerance, a || 255)
          : a || 255 // A
      }
    }
  }

  return new Image(width, height, newData, { alpha: 1 })
}

/**
 * Calculates smooth alpha for edge pixels
 */
function calculateSmoothAlpha(distance: number, tolerance: number, originalAlpha: number): number {
  const edgeRange = tolerance * 0.5
  if (distance > tolerance + edgeRange) {
    return originalAlpha
  } else if (distance > tolerance) {
    // Smooth transition
    const factor = (distance - tolerance) / edgeRange
    return Math.floor(originalAlpha * factor)
  }
  return 0
}

// Cap the multiplier so 3x phone screens don't request more pixels than they
// meaningfully render; 2x is the standard "retina" asset density.
const MAX_IMAGE_DEVICE_PIXEL_RATIO = 2

/**
 * Viewport width in device pixels (CSS width x devicePixelRatio, capped at 2x),
 * for sizing server-transformed images so retina displays get a sharp result.
 * Falls back to MAX_SAFE_INTEGER when no layout is available; the backend caps
 * the value to its largest supported width.
 */
export const getImageRequestViewportWidth = (): number => {
  if (typeof window === 'undefined' || !document.body.offsetWidth) {
    return Number.MAX_SAFE_INTEGER
  }
  return Math.round(
    document.body.offsetWidth * Math.min(window.devicePixelRatio || 1, MAX_IMAGE_DEVICE_PIXEL_RATIO)
  )
}
